// PLACEHOLDER: This file establishes the directory structure only.
// To be implemented in a future PR.

import supabase from "@/supabaseConfig"
import {
  AlertType,
  AlertDetails,
  AlertsResponse,
  AlertsStats,
  AlertDefinition,
  AlertActivation,
  AlertReview,
  AlertReviewAction,
} from "@/features/alerts/alertTypes"
import {
  AlertCategory,
  AlertDefinitionMetadata,
  AlertActivationMetadata,
} from "@/types/database.types"

class AlertsService {
  /**
   * Get current user's organization ID from the authenticated user
   */
  private async getCurrentOrgId(): Promise<string | null> {
    const {
      data: {user},
    } = await supabase.auth.getUser()
    if (!user) throw new Error("User not authenticated")

    const {data: orgUser} = await supabase
      .from("org_users")
      .select("org_id")
      .eq("auth_user_id", user.id)
      .single()

    return orgUser?.org_id || null
  }

  /**
   * Parse JSON metadata safely
   */
  private parseMetadata<T>(metadata: any): T | null {
    try {
      if (typeof metadata === "string") {
        return JSON.parse(metadata)
      }
      return metadata as T
    } catch (error) {
      console.warn("Failed to parse metadata:", error)
      return null
    }
  }

  /**
   * Transform database alert activation to AlertType
   */
  private async transformToAlertType(
    activation: AlertActivation & {
      alert_definitions: AlertDefinition
      threads?: {
        max_sensitivity: number | null
        title?: string | null
        thread_uid?: string
        group_id?: string
        message_groups?: {
          group_participants?:
            | {
                profiles?: {
                  name?: string | null
                  email?: string | null
                  phone_number?: string | null
                  channel_handle?: string | null
                } | null
              }[]
            | null
        } | null
      }
      firstMessageSubject?: string | null
    }
  ): Promise<AlertType> {
    // Parse metadata from alert definition
    const definitionMetadata = this.parseMetadata<AlertDefinitionMetadata>(
      activation.alert_definitions.metadata
    )

    // Count threads for this organization related to this alert
    const {count: threadCount} = await supabase
      .from("threads")
      .select("*", {count: "exact", head: true})
      .eq("org_id", activation.org_id)

    // Extract participants from the thread data
    let participants: string[] = []
    if (activation.threads?.message_groups?.group_participants) {
      participants = activation.threads.message_groups.group_participants
        .map((participant) => {
          const profile = participant?.profiles
          if (!profile) return null

          // Prioritize name, then email, then phone, then channel handle
          return (
            profile.name ||
            profile.email ||
            profile.phone_number ||
            profile.channel_handle ||
            null
          )
        })
        .filter((name): name is string => name !== null)
    }

    return {
      id: activation.activation_id,
      alertDefinitionId: activation.alert_definition_id,
      name: activation.alert_definitions.name,
      description: activation.alert_definitions.description || "",
      category: definitionMetadata?.category || "non_compliant_communications",
      riskLevel: definitionMetadata?.severity || "medium",
      status: activation.status,
      threadCount: threadCount || 0,
      timestamp: activation.created_at || new Date().toISOString(),
      messageId: activation.message_id,
      threadUid: activation.thread_uid,
      maxSensitivity: activation.threads?.max_sensitivity || null,
      participants,
      firstMessageSubject: activation.firstMessageSubject || null,
    }
  }

  /**
   * Fetch lightweight alerts for list view
   */
  async getAlerts(): Promise<AlertsResponse> {
    const orgId = await this.getCurrentOrgId()
    if (!orgId) throw new Error("No organization found for user")

    // Fetch alert activations with their definitions, threads, and related data
    const {data: activations, error} = await supabase
      .from("alert_activations")
      .select(
        `
        *,
        alert_definitions(*),
        threads(
          max_sensitivity,
          title,
          thread_uid,
          group_id,
          message_groups(
            group_participants(
              profiles(name, email, phone_number, channel_handle)
            )
          )
        )
      `
      )
      .eq("org_id", orgId)
      .order("created_at", {ascending: false})

    if (error) throw error

    // For each activation, get the first message subject
    const alertsWithDetails = await Promise.all(
      (activations || []).map(async (activation) => {
        let firstMessageSubject: string | null = null

        if (activation.thread_uid) {
          // Get the first message in the thread to get the subject
          const {data: firstMessage} = await supabase
            .from("thread_messages")
            .select(
              `
              messages(subject, message)
            `
            )
            .eq("thread_uid", activation.thread_uid)
            .order("created_at", {ascending: true})
            .limit(1)
            .single()

          firstMessageSubject = (firstMessage?.messages as any)?.subject || null
        }

        return {
          ...activation,
          firstMessageSubject,
        }
      })
    )

    // Transform the data
    const alerts: AlertType[] = await Promise.all(
      alertsWithDetails.map((activation) =>
        this.transformToAlertType(activation)
      )
    )

    // Remove duplicates based on alert name + messageId
    // const deduplicatedAlerts = this.removeDuplicateAlerts(alerts)
    // NOTE: Deduplication disabled to show all alerts, even duplicates

    // Separate resolved and unresolved alerts
    const resolvedStatuses = ["resolved", "dismissed"]
    const unresolvedAlerts = alerts.filter(
      (alert) => !resolvedStatuses.includes(alert.status)
    )
    const resolvedAlerts = alerts.filter((alert) =>
      resolvedStatuses.includes(alert.status)
    )

    return {
      unresolvedAlerts,
      resolvedAlerts,
      totalCount: alerts.length,
      unresolvedCount: unresolvedAlerts.length,
      resolvedCount: resolvedAlerts.length,
    }
  }

  /**
   * Get detailed alert information for a specific alert activation
   */
  async getAlertDetails(activationId: string): Promise<AlertDetails | null> {
    const orgId = await this.getCurrentOrgId()
    if (!orgId) throw new Error("No organization found for user")

    // Fetch the alert activation with all related data
    const {data: activation, error: activationError} = await supabase
      .from("alert_activations")
      .select(
        `
        *,
        alert_definitions(*),
        messages(*),
        threads(
          *,
          message_groups(
            group_participants(
              profiles(name, email, phone_number, channel_handle)
            )
          )
        ),
        alert_reviews(*)
      `
      )
      .eq("activation_id", activationId)
      .eq("org_id", orgId)
      .single()

    if (activationError || !activation) {
      console.error("Error fetching alert details:", activationError)
      return null
    }

    // Get first message subject if we have a thread
    let firstMessageSubject: string | null = null
    if (activation.thread_uid) {
      const {data: firstMessage} = await supabase
        .from("thread_messages")
        .select(
          `
          messages(subject, message)
        `
        )
        .eq("thread_uid", activation.thread_uid)
        .order("created_at", {ascending: true})
        .limit(1)
        .single()

      firstMessageSubject = (firstMessage?.messages as any)?.subject || null
    }

    // Parse metadata
    const activationMetadata = this.parseMetadata<AlertActivationMetadata>(
      activation.metadata
    )
    const definitionMetadata = this.parseMetadata<AlertDefinitionMetadata>(
      activation.alert_definitions.metadata
    )

    // Count threads for this organization
    const {count: threadCount} = await supabase
      .from("threads")
      .select("*", {count: "exact", head: true})
      .eq("org_id", orgId)

    // Transform to base AlertType first
    const baseAlert = await this.transformToAlertType({
      ...activation,
      threads: activation.threads
        ? {
            max_sensitivity: activation.threads.max_sensitivity,
            title: activation.threads.title,
            thread_uid: activation.threads.thread_uid,
            group_id: activation.threads.group_id,
            message_groups: activation.threads.message_groups,
          }
        : undefined,
      firstMessageSubject,
    })

    return {
      ...baseAlert,
      alertDefinition: activation.alert_definitions,
      activation,
      activationMetadata: activationMetadata || {
        reasoning: "",
        relevantExcerpts: [],
      },
      definitionMetadata: definitionMetadata || {
        riskLevel: "medium",
        positiveExamples: [],
        negativeExamples: [],
        llmGuidance: "",
        tags: [],
        category: "non_compliant_communications",
      },
      relatedMessage: activation.messages || undefined,
      relatedThread: activation.threads || undefined,
      reviews: activation.alert_reviews || [],
    }
  }

  /**
   * Get alert statistics
   */
  async getAlertStats(): Promise<AlertsStats> {
    const orgId = await this.getCurrentOrgId()
    if (!orgId) throw new Error("No organization found for user")

    // Get all activations for this org
    const {data: activations, error} = await supabase
      .from("alert_activations")
      .select("status")
      .eq("org_id", orgId)

    if (error) throw error

    const resolvedStatuses = ["resolved", "dismissed"]
    const unresolvedAlerts =
      activations?.filter((a) => !resolvedStatuses.includes(a.status)) || []
    const resolvedAlerts =
      activations?.filter((a) => resolvedStatuses.includes(a.status)) || []

    // Count escalated alerts
    const escalatedAlerts =
      activations?.filter((a) => a.status === "escalated") || []

    return {
      totalAlerts: activations?.length || 0,
      unresolvedAlerts: unresolvedAlerts.length,
      resolvedAlerts: resolvedAlerts.length,
      escalatedAlerts: escalatedAlerts.length,
    }
  }

  /**
   * Update alert status (for resolving/dismissing alerts)
   */
  async updateAlertStatus(
    activationId: string,
    status: "resolved" | "dismissed" | "escalated"
  ): Promise<boolean> {
    const orgId = await this.getCurrentOrgId()
    if (!orgId) throw new Error("No organization found for user")

    const {error} = await supabase
      .from("alert_activations")
      .update({status})
      .eq("activation_id", activationId)
      .eq("org_id", orgId)

    return !error
  }

  /**
   * Add a review to an alert
   */
  async addAlertReview(
    activationId: string,
    actionType: AlertReviewAction,
    comment?: string,
    assignedTo?: string
  ): Promise<boolean> {
    const orgId = await this.getCurrentOrgId()
    if (!orgId) throw new Error("No organization found for user")

    const {
      data: {user},
    } = await supabase.auth.getUser()
    if (!user) throw new Error("User not authenticated")

    const {error} = await supabase.from("alert_reviews").insert({
      activation_id: activationId,
      action_type: actionType,
      comment,
      assigned_to: assignedTo,
      org_id: orgId,
      user_id: user.id,
    })

    return !error
  }

  /**
   * Get alert definitions for the current organization
   */
  async getAlertDefinitions(): Promise<AlertDefinition[]> {
    const orgId = await this.getCurrentOrgId()
    if (!orgId) throw new Error("No organization found for user")

    // Fetch alert definitions for this organization or global ones
    const {data: alertDefinitions, error} = await supabase
      .from("alert_definitions")
      .select("*")
      .or(`org_id.eq.${orgId},scope.eq.global`)
      .eq("active", true)
      .order("name")

    if (error) throw error

    return alertDefinitions || []
  }

  /**
   * Remove duplicates based on alert name + messageId
   * Keeps the alert with the earliest timestamp for each duplicate group
   */
  private removeDuplicateAlerts(alerts: AlertType[]): AlertType[] {
    const duplicateGroups = new Map<string, AlertType[]>()

    // Group alerts by the key (alert name + messageId)
    for (const alert of alerts) {
      const key = `${alert.name}-${alert.messageId}`
      if (!duplicateGroups.has(key)) {
        duplicateGroups.set(key, [])
      }
      duplicateGroups.get(key)!.push(alert)
    }

    // For each group, keep only the one with the earliest timestamp
    const deduplicatedAlerts: AlertType[] = []
    for (const group of duplicateGroups.values()) {
      if (group.length === 1) {
        // No duplicates, keep the single alert
        deduplicatedAlerts.push(group[0])
      } else {
        // Find the alert with the earliest timestamp
        const earliest = group.reduce((earliest, current) => {
          return new Date(current.timestamp).getTime() <
            new Date(earliest.timestamp).getTime()
            ? current
            : earliest
        })
        deduplicatedAlerts.push(earliest)
      }
    }

    return deduplicatedAlerts
  }
}

// Export singleton instance
export const alertsService = new AlertsService()
